<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { getI18n } from '@/plugins/i18n'
import { useKpiMetricValue } from '@/composables/kpi-metric-value'
import { signalToneEnum, signalToneColor } from '@/composables/dashboard-kpi'
import { useCurrency } from '@/composables/currency'
import { useWebSiteStore } from '@/stores/web-site.store'
import SignalIcon from '@/components/shared/signal-bar/SignalIcon.vue'
import SignalValue from '@/components/shared/signal-bar/SignalValue.vue'
import SignalLabel from '@/components/shared/signal-bar/SignalLabel.vue'

const props = defineProps({
  metricKey: { type: String, required: true },
  filters: { type: Object, default: () => ({}) },
})

const { t } = getI18n()
const route = useRoute()
const webSiteStore = useWebSiteStore()
const { getCurrencyString } = useCurrency()
const { value, loading, metric } = useKpiMetricValue(() => props.metricKey, () => props.filters)

const tone = computed(() => metric.value?.tone?.(value.value ?? 0) ?? signalToneEnum.NEUTRAL)
const attention = computed(() => tone.value === signalToneEnum.ATTENTION)
const iconColor = computed(() => signalToneColor(tone.value))

const displayText = computed(() => {
  if (!metric.value) return ''
  const v = value.value ?? 0
  if (metric.value.unit === 'currency') {
    const code = webSiteStore.getDefaultCurrency()?.code
    if (code) return getCurrencyString(v, code)
  }
  return metric.value.format(v)
})

// Only link elsewhere: a metric that points at the page we are already on is not a link.
const linkTo = computed(() => {
  const link = metric.value?.link
  return link && link.name !== route.name ? link : null
})
</script>

<template>
  <div class="signal-metric">
    <q-spinner v-if="loading" size="xs" color="grey-5" />
    <template v-else>
      <signal-value :text="displayText" :tone="tone" />
      <signal-label :text="t(metric.labelKey)" :to="linkTo">
        <q-tooltip v-if="linkTo" :delay="400">{{ t(metric.linkLabelKey) }}</q-tooltip>
      </signal-label>
      <signal-icon v-if="attention" name="mdi-alert-outline" :color="iconColor" />
    </template>
  </div>
</template>

<style scoped>
.signal-metric {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  white-space: nowrap;
}
</style>
